import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3001/api',
  timeout: 25000,
  headers: { 'Content-Type': 'application/json' },
});

// Credential endpoints answer 401 for "wrong email/password" or "bad Google
// token". That is a normal failure for the form to display — not an expired
// session — so it must never trigger the refresh-and-retry flow below.
const CREDENTIAL_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/google'];

function isCredentialRequest(config) {
  const url = config?.url || '';
  return CREDENTIAL_ENDPOINTS.some((endpoint) => url.includes(endpoint));
}

let authToken = null;
let isRefreshing = false;
let refreshQueue = [];

export function setAuthToken(token) {
  authToken = token;
}

export function clearAuthToken() {
  authToken = null;
}

export function getAuthToken() {
  return authToken;
}

api.interceptors.request.use((config) => {
  if (authToken) {
    config.headers.Authorization = `Bearer ${authToken}`;
  }
  return config;
});

function getRefreshToken() {
  try {
    const stored = localStorage.getItem('biometric-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      return parsed.refreshToken || null;
    }
  } catch {}
  return null;
}

function storeNewTokens(accessToken, refreshToken) {
  try {
    const stored = localStorage.getItem('biometric-auth');
    const session = stored ? JSON.parse(stored) : {};
    session.token = accessToken;
    if (refreshToken) session.refreshToken = refreshToken;
    localStorage.setItem('biometric-auth', JSON.stringify(session));
    localStorage.setItem('auth_token', accessToken);
  } catch {}
}

function processQueue(error, token = null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  refreshQueue = [];
}

async function refreshTokens() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token available');

  const response = await axios.post(
    `${api.defaults.baseURL}/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const { accessToken, refreshToken: newRefreshToken } = response.data.data;
  return { accessToken, refreshToken: newRefreshToken };
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;

    if (
      error.response?.status !== 401 ||
      config?.url?.includes('/auth/refresh') ||
      isCredentialRequest(config)
    ) {
      if (error.response?.status === 401 && config?.url?.includes('/auth/refresh')) {
        clearAuthToken();
        window.dispatchEvent(new CustomEvent('auth:logout'));
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((token) => {
        config.headers.Authorization = `Bearer ${token}`;
        return api(config);
      }).catch(() => Promise.reject(error));
    }

    isRefreshing = true;

    try {
      const { accessToken, refreshToken } = await refreshTokens();
      storeNewTokens(accessToken, refreshToken);
      authToken = accessToken;
      processQueue(null, accessToken);
      config.headers.Authorization = `Bearer ${accessToken}`;
      return api(config);
    } catch (refreshError) {
      processQueue(refreshError);
      clearAuthToken();
      window.dispatchEvent(new CustomEvent('auth:logout'));
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }
);

export default api;
